'use client';

import useSWRInfinite from 'swr/infinite';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import { orderApi } from '@/entities/order';
import type { VisitCounselView, VisitCounselSearchQuery } from './visit-counsel.types';
import type { OrderStatus } from '@/entities/order/model/order.types';
import { isPlannerCounselHiddenByOrderStatus } from './planner-counsel-list-filters';

const PAGE_SIZE = 20;

export type MyVisitCounselListSortOrder = 'asc' | 'desc';

interface CounselPage {
  items: VisitCounselView[];
  hasNext: boolean;
  nextCursor: string | null;
  phoneByOrderId: Record<string, string>;
}

function buildQuery(
  sortOrder: MyVisitCounselListSortOrder,
  plannerId: string | undefined,
  cursor?: string | null,
  scheduleDateFrom?: string
) {
  const filterGroups: VisitCounselSearchQuery['filterGroups'] = [];
  if (plannerId) {
    filterGroups.push({
      logic: 'AND' as const,
      filters: [
        {
          field: 'counselorId' as const,
          operator: 'EQUALS' as const,
          value: plannerId,
          values: null,
        },
      ],
    });
  }
  if (scheduleDateFrom) {
    filterGroups.push({
      logic: 'AND' as const,
      filters: [
        {
          field: 'scheduleDate' as const,
          operator: 'RANGE_GTE' as const,
          value: scheduleDateFrom,
          values: null,
        },
      ],
    });
  }
  return {
    filterGroups,
    sortBy: 'scheduleDate' as const,
    sortOrder: sortOrder === 'asc' ? ('ASC' as const) : ('DESC' as const),
    pageSize: PAGE_SIZE,
    cursor: cursor ?? undefined,
  };
}

async function fetchPage(
  _key: readonly [
    string,
    MyVisitCounselListSortOrder,
    string | undefined,
    string | null | undefined,
    string | undefined,
  ]
): Promise<CounselPage> {
  const [, sortOrder, plannerId, cursor, scheduleDateFrom] = _key;
  const page = await myVisitCounselApi.searchMyVisitCounsels(
    buildQuery(sortOrder, plannerId, cursor, scheduleDateFrom)
  );

  if (page.items.length === 0) {
    return { items: [], hasNext: page.hasNext, nextCursor: page.nextCursor, phoneByOrderId: {} };
  }

  const uniqueOrderIds = [...new Set(page.items.map((c) => c.orderId))];

  const orderStatusMap = new Map<string, OrderStatus>();
  const phoneByOrderId: Record<string, string> = {};
  try {
    const orders = await orderApi.getOrdersByIds(uniqueOrderIds);
    for (const order of orders) {
      orderStatusMap.set(order.id, order.status);
      if (order.ordererPhone) phoneByOrderId[order.id] = order.ordererPhone;
    }
  } catch {
    // 주문 조회 실패 시 필터 없이 전체 반환
    return {
      items: page.items,
      hasNext: page.hasNext,
      nextCursor: page.nextCursor,
      phoneByOrderId,
    };
  }

  const filtered = page.items.filter((counsel) => {
    const status = orderStatusMap.get(counsel.orderId);
    if (!status) return true; // 주문 정보 없으면 표시 유지
    return !isPlannerCounselHiddenByOrderStatus(counsel, status);
  });

  return { items: filtered, hasNext: page.hasNext, nextCursor: page.nextCursor, phoneByOrderId };
}

export interface UseMyVisitCounselListReturn {
  items: VisitCounselView[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  /** orderId → 고객 전화번호 (주문 조회 성공 시 채워짐) */
  phoneByOrderId: Record<string, string>;
}

/**
 * PLANNER 전용: 본인에게 배정된 방문상담 목록 (cursor 페이지네이션, scheduleDate ASC 정렬)
 * 취소(ClosedLost/Unqualified/Requote) 또는 다른 상담으로 변경된 항목 제외
 * /api/v1/visit-counsels/search/my 사용 (PLANNER 전용)
 * @param plannerId - counselorId 필터 (서버 자동 주입이 안 될 때 FE에서 명시 전달)
 * @param scheduleDateFrom - 이 날짜 이후 상담만 조회 (YYYY-MM-DD, 포함). 미설정 시 전체 조회.
 */
export function useMyVisitCounselList(
  sortOrder: MyVisitCounselListSortOrder = 'asc',
  enabled: boolean = true,
  plannerId?: string,
  scheduleDateFrom?: string
): UseMyVisitCounselListReturn {
  const { data, size, setSize, isLoading, isValidating, error, mutate } =
    useSWRInfinite<CounselPage>(
      (pageIndex, previousPageData: CounselPage | null) => {
        if (!enabled) return null;
        if (pageIndex === 0)
          return ['my-visit-counsels-list', sortOrder, plannerId, null, scheduleDateFrom] as const;
        if (!previousPageData?.hasNext) return null;
        return [
          'my-visit-counsels-list',
          sortOrder,
          plannerId,
          previousPageData.nextCursor,
          scheduleDateFrom,
        ] as const;
      },
      fetchPage,
      {
        revalidateOnFocus: false,
        revalidateFirstPage: false,
        revalidateOnMount: true,
        // 전역 dedupingInterval(1h)이면 목록 재마운트·재검증도 네트워크까지 안 가서 이전 화면 복귀 시 캐시만 보임
        dedupingInterval: 0,
      }
    );

  const items = data?.flatMap((page) => page.items) ?? [];
  const lastPage = data?.[data.length - 1];
  const hasMore = lastPage?.hasNext ?? false;
  const phoneByOrderId = Object.assign({}, ...(data?.map((page) => page.phoneByOrderId) ?? []));

  const loadMore = () => {
    if (hasMore && !isValidating) setSize(size + 1);
  };

  return {
    items,
    isLoading: isLoading && !data,
    isLoadingMore: isValidating && !!data?.length,
    error: error ? (error instanceof Error ? error : new Error('상담 목록 조회 실패')) : null,
    hasMore,
    loadMore,
    refetch: () => mutate(),
    phoneByOrderId,
  };
}
